import { t } from "@lingui/macro";
import { useEffect, useState } from "react";
import { Platform } from "react-native";

import { Button } from "@whoosh/design-system/src/components/controls";
import {
  FaceIdIcon,
  FingerprintIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";
import { VStack, Text } from "@whoosh/design-system/src/components/primitive";

import { useFlags } from "../../utils/launchDarkly";
import { BiometricSignInButton } from "./BiometricSignInButton";
import {
  getBiometricUnlockType,
  isBiometricUnlockAvailable,
} from "./biometricUnlock";
import { getBiometricSignInPromptCopy } from "./getBiometricUnlockCopy";

type BiometricSignInPromptModalProps = {
  isOpen: boolean;
  onComplete: (clubSlug: string) => void;
  onDismiss: () => void;
  preferredClubSlug?: string | null;
};

export const BiometricSignInPromptModal = (
  props: BiometricSignInPromptModalProps
) => {
  const { biometricAuth } = useFlags();
  return biometricAuth === true ? (
    <BiometricSignInPromptModalContent {...props} />
  ) : null;
};

const BiometricSignInPromptModalContent = ({
  isOpen,
  onComplete,
  onDismiss,
  preferredClubSlug,
}: BiometricSignInPromptModalProps) => {
  const [isAvailable, setIsAvailable] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [biometricType, setBiometricType] =
    useState<Awaited<ReturnType<typeof getBiometricUnlockType>>>(null);

  useEffect(() => {
    let isMounted = true;

    const loadAvailability = async () => {
      try {
        const [available, type] = await Promise.all([
          isBiometricUnlockAvailable(),
          getBiometricUnlockType(),
        ]);

        if (isMounted) {
          setBiometricType(type);
          setIsAvailable(available);
          setIsLoading(false);
        }
      } catch {
        if (isMounted) {
          setIsAvailable(false);
          setIsLoading(false);
        }
      }
    };

    if (isOpen && Platform.OS !== "web") {
      void loadAvailability();
    } else {
      setIsLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  if (!isOpen || Platform.OS === "web" || isLoading || !isAvailable) {
    return null;
  }

  const Glyph = biometricType === "face" ? FaceIdIcon : FingerprintIcon;
  const { body, title } = getBiometricSignInPromptCopy(
    Platform.OS,
    biometricType
  );

  return (
    <StandardModal
      backgroundColorOverride="surface-primary"
      disableContentScroll
      hideCloseButton
      isOpen
      upperTitle={title}
      content={
        <VStack alignItems="center" px={6} py={4}>
          <Glyph color="icon-main-primary" height={48} width={48} />
          <Text mt={4} textAlign="center" variant="mainBaseMedium">
            {body}
          </Text>
        </VStack>
      }
      footerContent={
        <VStack gap={2} pb={6} px={6}>
          <BiometricSignInButton
            autoPrompt
            onComplete={onComplete}
            onCancel={onDismiss}
            preferredClubSlug={preferredClubSlug}
          />
          <Button
            accessibilityLabel={t`Sign in another way`}
            onPress={onDismiss}
            testID="biometric-sign-in-another-way"
            variant="ghost"
            width="100%"
          >
            {t`Sign in another way`}
          </Button>
        </VStack>
      }
    />
  );
};
